/**
 * Responsibility: summarise recent frame durations.
 *
 * A single frame time says nothing — it swings frame to frame. What is worth knowing is the average
 * and, more importantly, the **worst** frame in recent memory: one 40 ms hitch is felt where a
 * slightly higher average is not. Tile builds are exactly the kind of work that shows up as a rare
 * spike rather than a raised average, so the peak is the number that matters for tuning the build
 * budget.
 *
 * A fixed-size ring buffer, so it allocates nothing per frame and the window slides on its own.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

/** Milliseconds per second. */
const MS_PER_SECOND = 1000;

/**
 * Create a frame timer.
 *
 * @param {object} options
 * @param {number} options.sampleCount - Frames to remember. Must be at least 1.
 * @returns {object}
 */
export function createFrameTimer({ sampleCount }) {
  if (!Number.isInteger(sampleCount) || sampleCount < 1) {
    throw new Error('createFrameTimer: sampleCount must be an integer of at least 1');
  }

  const samples = new Float64Array(sampleCount);
  let filled = 0;
  let next = 0;

  return {
    /**
     * Record one frame.
     *
     * @param {number} seconds - Frame duration. Non-finite and non-positive values are ignored,
     *   because the first frame and the frame after a tab switch both produce nonsense.
     */
    record(seconds) {
      if (!Number.isFinite(seconds) || seconds <= 0) return;

      samples[next] = seconds;
      next = (next + 1) % sampleCount;
      if (filled < sampleCount) filled += 1;
    },

    /**
     * Summary of the window.
     *
     * @returns {{frames: number, averageMs: number, worstMs: number, fps: number}} All zero before
     *   any frame has been recorded, so callers need no special case for startup.
     */
    summary() {
      if (filled === 0) {
        return { frames: 0, averageMs: 0, worstMs: 0, fps: 0 };
      }

      let total = 0;
      let worst = 0;
      for (let i = 0; i < filled; i += 1) {
        total += samples[i];
        if (samples[i] > worst) worst = samples[i];
      }

      const average = total / filled;
      return {
        frames: filled,
        averageMs: average * MS_PER_SECOND,
        worstMs: worst * MS_PER_SECOND,
        fps: 1 / average,
      };
    },

    /** Forget everything. Call after a pause, so the stall is not counted as a bad frame. */
    reset() {
      filled = 0;
      next = 0;
    },
  };
}
